import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import api from '../../utils/api';
import { ListPageLayout } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function EducationExamMarks() {
  const [marks, setMarks] = useState([]);
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchCourses();
    }
  }, [selectedExam]);

  useEffect(() => {
    if (selectedExam && selectedCourse) {
      fetchMarks();
    }
  }, [page, rowsPerPage, selectedExam, selectedCourse]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0 && selectedExam && selectedCourse) {
        fetchMarks();
      } else if (selectedExam && selectedCourse) {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchExams = async () => {
    try {
      const response = await api.get('/education/exams', { params: { limit: 1000 } });
      setExams(response.data.data || []);
    } catch (err) {
      console.error('Error fetching exams:', err);
    }
  };

  const fetchCourses = async () => {
    try {
      const exam = exams.find((e) => e.id === parseInt(selectedExam));
      if (exam && exam.courses) {
        setCourses(exam.courses);
      } else {
        const response = await api.get('/education/courses', { params: { limit: 1000 } });
        setCourses(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchMarks = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/education/exam-marks', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          exam_id: selectedExam,
          course_id: selectedCourse,
          search: search || undefined,
        },
      });
      setMarks(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch marks');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleScoreChange = async (markId, newScore) => {
    try {
      await api.put(`/education/exam-marks/${markId}`, { score: parseFloat(newScore) });
      fetchMarks();
    } catch (err) {
      setError('Failed to update score');
    }
  };

  const getGradeColor = (grade) => {
    if (grade === 'A') return 'success';
    if (grade === 'B') return 'info';
    if (grade === 'C') return 'warning';
    if (grade === 'D') return 'default';
    return 'error';
  };

  return (
    <ListPageLayout
      title="Exam Marks"
      crumbs={
        <>
          Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Exam Marks</Box>
        </>
      }
      searchPlaceholder="Search..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      filters={
        <>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select Exam</InputLabel>
            <Select
              value={selectedExam}
              onChange={(e) => {
                setSelectedExam(e.target.value);
                setSelectedCourse('');
                setPage(0);
              }}
              label="Select Exam"
            >
              {exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedExam}>
            <InputLabel>Select Course</InputLabel>
            <Select
              value={selectedCourse}
              onChange={(e) => {
                setSelectedCourse(e.target.value);
                setPage(0);
              }}
              label="Select Course"
            >
              {courses.map((course) => (
                <MenuItem key={course.id} value={course.id}>
                  {course.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      }
    >
      {!selectedExam || !selectedCourse ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Please select an exam and course to view marks.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No records yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    marks.map((mark) => (
                      <TableRow key={mark.id} hover>
                        <TableCell>{mark.member_id}</TableCell>
                        <TableCell>
                          {mark.first_name} {mark.last_name}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            value={mark.score || ''}
                            onChange={(e) => {
                              const newScore = e.target.value;
                              setMarks((prev) =>
                                prev.map((m) =>
                                  m.id === mark.id ? { ...m, score: newScore } : m
                                )
                              );
                            }}
                            onBlur={(e) => handleScoreChange(mark.id, e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          {mark.grade && (
                            <Chip
                              label={mark.grade}
                              color={getGradeColor(mark.grade)}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {mark.status && (
                            <Chip
                              label={mark.status}
                              color={mark.status === 'pass' ? 'success' : 'error'}
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </TableContainer>
      )}
    </ListPageLayout>
  );
}
